const base64UrlToUint8Array = (base64url) => {
  const base64 = String(base64url).replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - base64.length % 4) % 4), '=');
  const binary = window.atob(padded);
  return Uint8Array.from(binary, char => char.charCodeAt(0));
};

const arrayBufferToBase64Url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const normalizeRequestOptions = (options) => {
  const publicKey = { ...options };

  if (typeof publicKey.challenge === 'string') {
    publicKey.challenge = base64UrlToUint8Array(publicKey.challenge);
  }

  if (Array.isArray(publicKey.allowCredentials)) {
    publicKey.allowCredentials = publicKey.allowCredentials.map(credential => ({
      ...credential,
      id: typeof credential.id === 'string' ? base64UrlToUint8Array(credential.id) : credential.id,
    }));
  }

  return publicKey;
};

const normalizeCreationOptions = (options) => {
  const publicKey = { ...options };

  if (typeof publicKey.challenge === 'string') {
    publicKey.challenge = base64UrlToUint8Array(publicKey.challenge);
  }

  if (publicKey.user && typeof publicKey.user.id === 'string') {
    publicKey.user = {
      ...publicKey.user,
      id: base64UrlToUint8Array(publicKey.user.id),
    };
  }

  if (Array.isArray(publicKey.excludeCredentials)) {
    publicKey.excludeCredentials = publicKey.excludeCredentials.map(credential => ({
      ...credential,
      id: typeof credential.id === 'string' ? base64UrlToUint8Array(credential.id) : credential.id,
    }));
  }

  return publicKey;
};

const getPublicKeyOptions = (payload) => payload?.publicKey || payload?.options || payload;

export const isPasskeySupported = () => (
  typeof window !== 'undefined'
  && window.isSecureContext
  && 'PublicKeyCredential' in window
  && navigator.credentials
);

export const createPasskeyAssertion = async (optionsPayload) => {
  if (!isPasskeySupported()) {
    throw new Error('Passkey sign-in is not supported on this browser.');
  }

  const publicKey = normalizeRequestOptions(getPublicKeyOptions(optionsPayload));
  const credential = await navigator.credentials.get({ publicKey });

  if (!credential) {
    throw new Error('Passkey sign-in was cancelled.');
  }

  return {
    id: credential.id,
    rawId: arrayBufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: arrayBufferToBase64Url(credential.response.authenticatorData),
      clientDataJSON: arrayBufferToBase64Url(credential.response.clientDataJSON),
      signature: arrayBufferToBase64Url(credential.response.signature),
      userHandle: credential.response.userHandle
        ? arrayBufferToBase64Url(credential.response.userHandle)
        : null,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
    authenticatorAttachment: credential.authenticatorAttachment || null,
  };
};

export const createPasskeyAttestation = async (optionsPayload) => {
  if (!isPasskeySupported()) {
    throw new Error('Passkeys are not supported on this browser.');
  }

  const publicKey = normalizeCreationOptions(getPublicKeyOptions(optionsPayload));
  const credential = await navigator.credentials.create({ publicKey });

  if (!credential) {
    throw new Error('Passkey enrollment was cancelled.');
  }

  const transports = typeof credential.response.getTransports === 'function'
    ? credential.response.getTransports()
    : [];

  return {
    id: credential.id,
    rawId: arrayBufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: arrayBufferToBase64Url(credential.response.attestationObject),
      clientDataJSON: arrayBufferToBase64Url(credential.response.clientDataJSON),
    },
    clientExtensionResults: credential.getClientExtensionResults(),
    authenticatorAttachment: credential.authenticatorAttachment || null,
    transports,
  };
};
